'use client';

import { useEffect, useRef } from 'react';

import { ActivityType, type AddImpressionBody, type ImpressionRes, type ImpressionWrapperProps } from '../../types/impressions';

import { useImpression } from './impression-provider';

/** Timeout for the fire-and-forget POST. Matches the upstream `/api/v2/impressions/addImpression` route's BE-forwarding timeout. */
const IMPRESSION_REQUEST_TIMEOUT_MS = 15000;

/**
 * Fire-and-forget POST to the per-app engagement route. Errors / non-OK responses are silently swallowed —
 * a failed impression should never surface as a UI error. Returns the parsed body on success, `null` on
 * any failure (network error, timeout, non-2xx, parse failure).
 */
const sendImpression = async (data: AddImpressionBody): Promise<ImpressionRes | null> => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), IMPRESSION_REQUEST_TIMEOUT_MS);
	try {
		const response = await fetch('/api/v2/impressions/addImpression', {
			method: 'POST',
			body: JSON.stringify(data),
			signal: controller.signal
		});
		if (!response.ok) return null;
		return await response.json();
	} catch {
		return null;
	} finally {
		clearTimeout(timer);
	}
};

/**
 * Passive engagement observer around an interactive child (a card, a link, an image). Tracks VIEW + CLICK
 * activity and dedupes per `(moduleFor, moduleId)` pair within a component instance.
 *
 * VIEW: an IntersectionObserver (threshold = `impressionThreshold`, default 0.5) fires once when the element
 * first crosses the threshold for a given `(moduleFor, moduleId)`. After firing, the observer disconnects
 * for that pair; the per-pair Set entry blocks any subsequent fire for the same id even if the effect
 * re-runs. When `moduleId` changes (e.g. a rotating ad cycles to a new creative) the effect re-runs, the
 * dedupe Set is checked: a new id attaches a fresh observer; an already-fired id skips observation entirely.
 * Net effect on `HpMarketingAd`: one VIEW per unique ad creative per page session, no matter how many times
 * the rotator cycles through it.
 *
 * CLICK: `onClick` + `onAuxClick` (middle-click only, `button === 1`) — left + middle clicks are counted,
 * right-clicks (button 2) are not. Each `(moduleFor, moduleId)` pair fires CLICK at most once per instance.
 * Both VIEW and CLICK paths guard against `userId === ''` (the anon-init race).
 *
 * The wrapper is intentionally NOT itself interactive — it's a layout box that observes clicks bubbling up
 * from whatever interactive child the caller renders. `role="button"` / `tabIndex` would mislead assistive
 * tech, hence the explicit `eslint-disable` on the rendered `<div>`.
 */
export const ImpressionWrapper = ({
	moduleId,
	moduleFor,
	children,
	className,
	disableClickTrack,
	disableImpressionTrack,
	impressionThreshold = 0.5
}: ImpressionWrapperProps) => {
	if (impressionThreshold < 0 || impressionThreshold > 1) {
		throw new Error('impressionThreshold must be between 0 and 1');
	}

	const { userId, isLocalhost } = useImpression();
	const wrapperRef = useRef<HTMLDivElement>(null);
	// Per-instance dedupe sets keyed on `${moduleFor}:${moduleId}`. Sets (not booleans) so a single wrapper
	// can correctly dedupe across rotating ids — see HpMarketingAd's rotator.
	const firedImpressionsRef = useRef<Set<string>>(new Set());
	const firedClicksRef = useRef<Set<string>>(new Set());
	const dedupeKey = `${moduleFor}:${moduleId}`;

	useEffect(() => {
		if (disableImpressionTrack || isLocalhost) return;
		// Already fired for this id — skip observation entirely; no observer attaches.
		if (firedImpressionsRef.current.has(dedupeKey)) return;
		const el = wrapperRef.current;
		if (!el) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (!entry?.isIntersecting) return;
				if (entry.intersectionRatio < impressionThreshold) return;
				if (firedImpressionsRef.current.has(dedupeKey)) return;
				if (!userId) return; // Race guard — wait until provider populates userId.
				firedImpressionsRef.current.add(dedupeKey);
				void sendImpression({
					user_key: userId,
					activity_type: ActivityType.VIEW,
					module_for: moduleFor,
					module_uuid: moduleId
				});
				observer.disconnect();
			},
			{ threshold: [impressionThreshold] }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [disableImpressionTrack, impressionThreshold, userId, moduleFor, moduleId, isLocalhost, dedupeKey]);

	const fireClick = () => {
		if (isLocalhost) return;
		if (disableClickTrack) return;
		if (firedClicksRef.current.has(dedupeKey)) return;
		if (!userId) return;
		firedClicksRef.current.add(dedupeKey);
		void sendImpression({
			user_key: userId,
			activity_type: ActivityType.CLICK,
			module_for: moduleFor,
			module_uuid: moduleId
		});
	};

	const handleClick = () => fireClick();
	const handleAuxClick = (e: React.MouseEvent) => {
		// Middle-click only — right-click also fires `auxclick` (button 2) but is not engagement.
		if (e.button === 1) fireClick();
	};

	return (
		// `jsx-a11y/click-events-have-key-events` + `no-static-element-interactions` disabled on purpose:
		// this wrapper is a PASSIVE observer of clicks bubbling up from interactive children. It is NOT
		// itself an interactive control — adding `role="button"` + tabIndex + keyboard handlers would
		// mislead assistive tech.
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		<div ref={wrapperRef} onClick={handleClick} onAuxClick={handleAuxClick} className={className}>
			{children}
		</div>
	);
};
